import React, { useEffect, useRef, useState } from "react";
import { X, ChevronLeft } from "lucide-react";
import type {
  CameraStatus,
  DetectionDebugValues,
  PaperCameraParams,
} from "./usePaperCamera";
import PaperCameraDebug from "./PaperCameraDebug";
import { ImageCropper } from "./ImageCropper";

type FullParams = PaperCameraParams;

type View = "picker" | "camera" | "preview" | "crop" | "uploaded_images";

const previewIconBtnStyle: React.CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 8,
  background: "rgba(0,0,0,0.55)",
  border: "1px solid rgba(255,255,255,0.15)",
  color: "white",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  backdropFilter: "blur(6px)",
  WebkitBackdropFilter: "blur(6px)",
};

interface AddImageModalProps {
  open: boolean;
  onClose: () => void;

  // Camera props (forwarded from usePaperCamera)
  cameraStatus: CameraStatus;
  countdown: number;
  autoCaptureCountdown: number;
  detectionScore: number;
  debugValues: DetectionDebugValues;
  lastCapturedImage: string | null;
  videoRef: React.RefObject<HTMLVideoElement>;
  edgeCanvasRef: React.RefObject<HTMLCanvasElement>;
  params: FullParams;
  setParams: React.Dispatch<React.SetStateAction<FullParams>>;
  openCamera: () => void;
  closeCamera: () => void;
  captureManually: () => void;

  /** Called when user confirms adding the image. Receives base64-encoded data. */
  onConfirm: (b64: string) => void;

  debugMode?: boolean;

  /** Override the default modal title */
  title?: string;

  /** Uploaded attachments waiting for next agent turn (notepad only) */
  attachments?: { id: string; dataUrl: string }[];
  onRemoveAttachment?: (id: string, dataUrl: string) => void;
  /** Total number of already-added images (scratchpad passes this directly) */
  attachmentCount?: number;
}

async function isLowQuality(dataUrl: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const SIZE = 128;
      const canvas = document.createElement("canvas");
      canvas.width = SIZE;
      canvas.height = SIZE;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(false);
        return;
      }
      ctx.drawImage(img, 0, 0, SIZE, SIZE);
      const { data } = ctx.getImageData(0, 0, SIZE, SIZE);

      // Grayscale array
      const gray = new Float32Array(SIZE * SIZE);
      for (let i = 0; i < SIZE * SIZE; i++) {
        gray[i] =
          0.299 * data[i * 4] +
          0.587 * data[i * 4 + 1] +
          0.114 * data[i * 4 + 2];
      }

      // Laplacian variance: sharp images have many strong edges so variance is high;
      // blurry or dark images smooth those edges out so variance is low.
      let sum = 0;
      let sumSq = 0;
      const count = (SIZE - 2) * (SIZE - 2);
      for (let y = 1; y < SIZE - 1; y++) {
        for (let x = 1; x < SIZE - 1; x++) {
          const lap =
            -4 * gray[y * SIZE + x] +
            gray[(y - 1) * SIZE + x] +
            gray[(y + 1) * SIZE + x] +
            gray[y * SIZE + (x - 1)] +
            gray[y * SIZE + (x + 1)];
          sum += lap;
          sumSq += lap * lap;
        }
      }
      const mean = sum / count;
      const variance = sumSq / count - mean * mean;
      // Handwriting on paper typically scores 1000+; blurry/dark images score < 100
      resolve(variance < 250);
    };
    img.onerror = () => resolve(false);
    img.src = dataUrl;
  });
}

export default function AddImageModal({
  open,
  onClose,
  cameraStatus,
  countdown,
  autoCaptureCountdown,
  detectionScore,
  debugValues,
  lastCapturedImage,
  videoRef,
  edgeCanvasRef,
  params,
  setParams,
  openCamera,
  closeCamera,
  captureManually,
  onConfirm,
  debugMode = false,
  title = "Add image",
  attachments,
  onRemoveAttachment,
  attachmentCount,
}: AddImageModalProps) {
  const [view, setView] = useState<View>("picker");
  const [debugOpen, setDebugOpen] = useState(false);
  const [pendingDataUrl, setPendingDataUrl] = useState<string | null>(null);
  const [originalDataUrl, setOriginalDataUrl] = useState<string | null>(null);
  const [lowQualityWarning, setLowQualityWarning] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const lowQualityCheckRef = useRef<{ cancelled: boolean }>({
    cancelled: false,
  });

  // When auto-capture fires (capturing → active), advance to crop view
  const prevStatusRef = useRef<CameraStatus>("idle");
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = cameraStatus;
    if (
      prev === "capturing" &&
      cameraStatus === "active" &&
      view === "camera" &&
      lastCapturedImage
    ) {
      setPendingDataUrl(lastCapturedImage);
      setOriginalDataUrl(lastCapturedImage);
      closeCamera();
      setView("crop");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraStatus]);

  // Reset when modal closes + lock body scroll while open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      setView("picker");
      setDebugOpen(false);
      setPendingDataUrl(null);
      setOriginalDataUrl(null);
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const handleClose = () => {
    lowQualityCheckRef.current.cancelled = true;
    if (view === "camera") closeCamera();
    setView("picker");
    setPendingDataUrl(null);
    setOriginalDataUrl(null);
    setLowQualityWarning(false);
    onClose();
  };

  const confirmImageAndPreview = (dataUrl: string) => {
    lowQualityCheckRef.current.cancelled = true;
    const token = { cancelled: false };
    lowQualityCheckRef.current = token;
    setPendingDataUrl(dataUrl);
    setView("preview");
    setLowQualityWarning(false);
    isLowQuality(dataUrl).then((result) => {
      if (!token.cancelled) setLowQualityWarning(result);
    });
  };

  const handleTakePhoto = () => {
    openCamera();
    setView("camera");
  };

  const handleBack = () => {
    if (view === "preview") {
      setView("crop");
    } else {
      closeCamera();
      setView("picker");
    }
  };

  const ALLOWED_FILE_TYPES = ["image/jpeg", "image/png", "image/webp"];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_FILE_TYPES.includes(file.type)) return;
    const reader = new FileReader();
    reader.onload = () => {
      const url = reader.result as string;
      setPendingDataUrl(url);
      setOriginalDataUrl(url);
      setView("crop");
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleCropped = (croppedDataUrl: string) => {
    confirmImageAndPreview(croppedDataUrl);
  };

  const handleSkipCrop = () => {
    if (originalDataUrl) confirmImageAndPreview(originalDataUrl);
  };

  const handleConfirm = () => {
    if (pendingDataUrl) {
      const b64 = pendingDataUrl.split(",")[1] ?? pendingDataUrl;
      onConfirm(b64);
    }
    handleClose();
  };

  const applyPreviewTransform = (kind: "rotate" | "flipH" | "flipV") => {
    if (!pendingDataUrl) return;
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      if (kind === "rotate") {
        canvas.width = img.height;
        canvas.height = img.width;
        ctx.translate(canvas.width, 0);
        ctx.rotate(Math.PI / 2);
      } else {
        canvas.width = img.width;
        canvas.height = img.height;
        if (kind === "flipH") {
          ctx.translate(canvas.width, 0);
          ctx.scale(-1, 1);
        } else {
          ctx.translate(0, canvas.height);
          ctx.scale(1, -1);
        }
      }
      ctx.drawImage(img, 0, 0);
      setPendingDataUrl(canvas.toDataURL("image/jpeg", 0.92));
    };
    img.src = pendingDataUrl;
  };

  const resolvedCount = attachmentCount ?? attachments?.length ?? 0;
  const atLimit = resolvedCount >= 5;

  const isPaperDetected = cameraStatus === "detected";
  const isCapturing =
    cameraStatus === "capturing" || cameraStatus === "captured";
  const cameraActive =
    cameraStatus === "active" ||
    cameraStatus === "detected" ||
    cameraStatus === "capturing";

  const cameraHint = !cameraActive
    ? "Starting camera…"
    : isPaperDetected
      ? "Hold steady…"
      : "Hold your work to cover the center box";

  if (!open) return null;

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.45)",
          zIndex: 300,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          // Reserve space at the bottom so the modal clears the fixed
          // "Hold to speak" bar that sits over the notepad view.
          paddingBottom: 120,
        }}
        onClick={handleClose}
      >
        {/* Modal card */}
        <div
          style={{
            background: "white",
            borderRadius: 16,
            width: view === "camera" && debugOpen ? 540 : 380,
            maxWidth: "calc(100vw - 32px)",
            maxHeight: "calc(100vh - 168px)",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
            overflow: "hidden",
            position: "relative",
            transition: "width 0.2s ease",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          {attachments ? (
            // Tabbed header (notepad): tabs on left, × on right
            // For sub-views (camera/preview/crop): back + sub-title + ×
            view === "picker" || view === "uploaded_images" ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "stretch",
                  justifyContent: "space-between",
                  padding: "0 12px 0 0",
                  flexShrink: 0,
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <div style={{ display: "flex" }}>
                  {(["picker", "uploaded_images"] as const).map((tab) => {
                    const isActive = view === tab;
                    const label =
                      tab === "picker"
                        ? "Add image"
                        : `Uploaded${attachments.length > 0 ? ` (${attachments.length})` : ""}`;
                    return (
                      <button
                        key={tab}
                        onClick={() => setView(tab)}
                        style={{
                          background: "none",
                          border: "none",
                          borderBottom: isActive
                            ? "2px solid #1C9C6D"
                            : "2px solid transparent",
                          cursor: "pointer",
                          padding: "16px 16px 14px",
                          fontSize: 14,
                          fontWeight: isActive ? 600 : 400,
                          color: isActive ? "#1C9C6D" : "#888",
                          transition: "color 0.15s, border-color 0.15s",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={handleClose}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "0 4px",
                    color: "#999",
                    fontSize: 18,
                    lineHeight: 1,
                    borderRadius: 4,
                    flexShrink: 0,
                  }}
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>
            ) : (
              // Sub-view header: ← back + sub-title + ×
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "16px 16px 14px",
                  flexShrink: 0,
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button
                    onClick={handleBack}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 4,
                      color: "#888",
                      borderRadius: 4,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <span
                    style={{ fontSize: 15, fontWeight: 600, color: "#111" }}
                  >
                    {view === "camera"
                      ? "Take a photo"
                      : view === "crop"
                        ? "Crop image"
                        : "Preview"}
                  </span>
                </div>
                <button
                  onClick={handleClose}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 4,
                    color: "#999",
                    borderRadius: 4,
                    display: "flex",
                    alignItems: "center",
                  }}
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>
            )
          ) : (
            // Simple header (scratchpad): title + ×
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "20px 20px 16px",
                  flexShrink: 0,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {(view === "camera" ||
                    view === "crop" ||
                    view === "preview") && (
                    <button
                      onClick={handleBack}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 4,
                        color: "#888",
                        borderRadius: 4,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <ChevronLeft size={20} />
                    </button>
                  )}
                  <span
                    style={{ fontSize: 17, fontWeight: 600, color: "#111" }}
                  >
                    {title}
                  </span>
                </div>
                <button
                  onClick={handleClose}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 4,
                    color: "#999",
                    borderRadius: 4,
                    display: "flex",
                    alignItems: "center",
                  }}
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>
              <div
                style={{ height: 1, background: "#f0f0f0", flexShrink: 0 }}
              />
            </>
          )}

          {/* ── Picker ── */}
          {view === "picker" && (
            <div
              style={{
                padding: "16px 20px 20px",
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <button
                onClick={atLimit ? undefined : handleTakePhoto}
                disabled={atLimit}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: "14px 16px",
                  borderRadius: 12,
                  border: "1.5px solid #e8e8e8",
                  background: "white",
                  cursor: atLimit ? "not-allowed" : "pointer",
                  textAlign: "left",
                  transition: "border-color 0.15s, background 0.15s",
                  opacity: atLimit ? 0.45 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!atLimit) {
                    e.currentTarget.style.borderColor = "#1C9C6D";
                    e.currentTarget.style.background = "#f8fffe";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e8e8e8";
                  e.currentTarget.style.background = "white";
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    background: "rgba(28,156,109,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <svg width="22" height="19" viewBox="0 0 22 19" fill="none">
                    <path
                      d="M8 2H14L16 5H20C20.55 5 21 5.45 21 6V17C21 17.55 20.55 18 20 18H2C1.45 18 1 17.55 1 17V6C1 5.45 1.45 5 2 5H6L8 2Z"
                      stroke="#1C9C6D"
                      strokeWidth="1.7"
                      strokeLinejoin="round"
                      fill="none"
                    />
                    <circle
                      cx="11"
                      cy="11.5"
                      r="3.5"
                      stroke="#1C9C6D"
                      strokeWidth="1.7"
                      fill="none"
                    />
                  </svg>
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: "#111",
                      marginBottom: 2,
                    }}
                  >
                    Take a photo
                  </div>
                  <div style={{ fontSize: 13, color: "#888" }}>
                    Use your device camera
                  </div>
                </div>
              </button>

              <div style={{ textAlign: "center", fontSize: 13, color: "#aaa" }}>
                or
              </div>

              <button
                onClick={
                  atLimit ? undefined : () => fileInputRef.current?.click()
                }
                disabled={atLimit}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: "14px 16px",
                  borderRadius: 12,
                  border: "1.5px solid #e8e8e8",
                  background: "white",
                  cursor: atLimit ? "not-allowed" : "pointer",
                  textAlign: "left",
                  transition: "border-color 0.15s, background 0.15s",
                  opacity: atLimit ? 0.45 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!atLimit) {
                    e.currentTarget.style.borderColor = "#4A90E2";
                    e.currentTarget.style.background = "#f5f8ff";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e8e8e8";
                  e.currentTarget.style.background = "white";
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    background: "rgba(74,144,226,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M10 2V13M10 2L6.5 5.5M10 2L13.5 5.5"
                      stroke="#4A90E2"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M2 14V16C2 17.1 2.9 18 4 18H16C17.1 18 18 17.1 18 16V14"
                      stroke="#4A90E2"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: "#111",
                      marginBottom: 2,
                    }}
                  >
                    Upload from device
                  </div>
                  <div style={{ fontSize: 13, color: "#888" }}>
                    JPEG, PNG, WebP
                  </div>
                </div>
              </button>

              {(attachments !== undefined || attachmentCount !== undefined) && (
                <div
                  style={{
                    textAlign: "center",
                    fontSize: 13,
                    color: atLimit ? "#e05050" : "#aaa",
                    fontWeight: atLimit ? 600 : 400,
                    marginTop: 2,
                  }}
                >
                  {resolvedCount} / 5 images attached
                </div>
              )}
            </div>
          )}

          {/* ── Camera ── */}
          {view === "camera" && (
            <div
              style={{
                padding: "16px 20px 20px",
                display: "flex",
                flexDirection: "column",
                gap: 12,
                overflowY: "auto",
              }}
            >
              <div
                style={{
                  borderRadius: 10,
                  overflow: "hidden",
                  background: "#000",
                  aspectRatio: "4/3",
                  position: "relative",
                }}
              >
                <video
                  ref={videoRef}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                  playsInline
                  muted
                  autoPlay
                />
                {isPaperDetected && (
                  <div
                    className="animate-scan-line absolute inset-x-0 z-[9] h-px pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(90deg, transparent, rgba(74,222,128,0.9) 40%, rgba(74,222,128,0.9) 60%, transparent)",
                    }}
                  />
                )}
                {isCapturing && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "rgba(255,255,255,0.75)",
                      zIndex: 10,
                      pointerEvents: "none",
                    }}
                  />
                )}
                {cameraActive && autoCaptureCountdown > 0 && (
                  <div
                    style={{
                      position: "absolute",
                      top: 10,
                      left: 10,
                      minWidth: 44,
                      height: 44,
                      padding: "0 12px",
                      borderRadius: 22,
                      background: "rgba(0,0,0,0.7)",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 22,
                      fontWeight: 700,
                      zIndex: 15,
                      pointerEvents: "none",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.5)",
                      fontVariantNumeric: "tabular-nums",
                      border: "1px solid rgba(255,255,255,0.15)",
                    }}
                  >
                    {autoCaptureCountdown}
                  </div>
                )}
                {isPaperDetected && countdown > 0 && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 11,
                      pointerEvents: "none",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 72,
                        fontWeight: 700,
                        color: "white",
                        textShadow: "0 2px 16px rgba(0,0,0,0.9)",
                        lineHeight: 1,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {countdown}
                    </span>
                  </div>
                )}
                {cameraStatus !== "denied" && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      padding: "24px 12px 10px",
                      background:
                        "linear-gradient(transparent, rgba(0,0,0,0.6))",
                      zIndex: 12,
                      pointerEvents: "none",
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        fontSize: 13,
                        textAlign: "center",
                        transition: "color 0.3s",
                        color: isPaperDetected
                          ? "rgb(74,222,128)"
                          : "rgba(255,255,255,0.85)",
                      }}
                    >
                      {cameraHint}
                    </p>
                  </div>
                )}
                {cameraStatus === "denied" && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "rgba(0,0,0,0.75)",
                      zIndex: 20,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "20px",
                      gap: 12,
                      borderRadius: 10,
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        fontSize: 15,
                        fontWeight: 600,
                        color: "#ff6b6b",
                        textAlign: "center",
                      }}
                    >
                      Camera access denied
                    </p>
                    <button
                      onClick={openCamera}
                      style={{
                        marginTop: 4,
                        padding: "9px 20px",
                        borderRadius: 8,
                        border: "1.5px solid rgba(255,255,255,0.4)",
                        background: "rgba(255,255,255,0.12)",
                        color: "white",
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      Try again
                    </button>
                  </div>
                )}
                {isPaperDetected && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      borderRadius: 10,
                      boxShadow: "inset 0 0 0 3px rgba(74,222,128,0.8)",
                      zIndex: 8,
                      pointerEvents: "none",
                    }}
                  />
                )}
                {cameraActive && !isCapturing && (
                  <div
                    style={{
                      position: "absolute",
                      left: `${((1 - params.guideW) / 2) * 100}%`,
                      top: `${((1 - params.guideH) / 2) * 100}%`,
                      width: `${params.guideW * 100}%`,
                      height: `${params.guideH * 100}%`,
                      border: "2px dashed rgba(74,222,128,0.9)",
                      borderRadius: 6,
                      zIndex: 9,
                      pointerEvents: "none",
                      boxShadow: "0 0 0 9999px rgba(0,0,0,0.18)",
                    }}
                  />
                )}
                {debugMode && (
                  <button
                    onClick={() => setDebugOpen((o) => !o)}
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      zIndex: 13,
                      height: 22,
                      padding: "0 7px",
                      borderRadius: 4,
                      background: debugOpen
                        ? "rgba(74,222,128,0.3)"
                        : "rgba(0,0,0,0.55)",
                      border: `1px solid ${debugOpen ? "rgba(74,222,128,0.6)" : "transparent"}`,
                      cursor: "pointer",
                      color: debugOpen
                        ? "rgb(74,222,128)"
                        : "rgba(255,255,255,0.6)",
                      fontSize: 10,
                      fontFamily: "monospace",
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                    }}
                    aria-label="Toggle debug panel"
                  >
                    D
                  </button>
                )}
              </div>

              <button
                onClick={captureManually}
                disabled={!cameraActive}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "11px 16px",
                  borderRadius: 10,
                  border: "1.5px solid #e8e8e8",
                  background: "white",
                  cursor: cameraActive ? "pointer" : "default",
                  fontSize: 14,
                  color: "#444",
                  transition: "border-color 0.15s",
                  opacity: cameraActive ? 1 : 0.45,
                }}
                onMouseEnter={(e) => {
                  if (cameraActive) e.currentTarget.style.borderColor = "#bbb";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e8e8e8";
                }}
              >
                Capture manually
              </button>

              {/* Debug panel — inline below camera when open */}
              {debugMode && debugOpen && (
                <PaperCameraDebug
                  detectionScore={detectionScore}
                  debugValues={debugValues}
                  lastCapturedImage={lastCapturedImage}
                  params={params}
                  setParams={setParams}
                  edgeCanvasRef={edgeCanvasRef}
                  onClose={() => setDebugOpen(false)}
                />
              )}
            </div>
          )}

          {/* ── Crop ── */}
          {view === "crop" && pendingDataUrl && (
            <div
              style={{
                padding: "16px 20px 20px",
                display: "flex",
                flexDirection: "column",
                gap: 12,
                overflowY: "auto",
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: 13,
                  color: "#888",
                  textAlign: "center",
                }}
              >
                Drag the corners or selection to crop
              </p>
              <ImageCropper dataUrl={originalDataUrl!} onCrop={handleCropped} />
              <button
                onClick={handleSkipCrop}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  padding: "11px 16px",
                  borderRadius: 10,
                  border: "1.5px solid #e8e8e8",
                  background: "white",
                  cursor: "pointer",
                  fontSize: 14,
                  color: "#666",
                  transition: "border-color 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#bbb";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e8e8e8";
                }}
              >
                Skip crop
              </button>
            </div>
          )}

          {/* ── Preview ── */}
          {view === "preview" && pendingDataUrl && (
            <div
              style={{
                padding: "16px 20px 20px",
                display: "flex",
                flexDirection: "column",
                gap: 14,
                overflow: "hidden",
              }}
            >
              {/* Image preview card — the image inside caps its height
                  against the viewport so it shrinks on short screens while
                  always keeping its natural aspect ratio. */}
              <div
                style={{
                  borderRadius: 12,
                  border: "1.5px solid #e8e8e8",
                  overflow: "hidden",
                  background: "#fafafa",
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={pendingDataUrl}
                  alt="Preview"
                  style={{
                    maxWidth: "100%",
                    // Leave room for modal chrome + text + buttons + the
                    // 168px safe-area we reserved on the backdrop.
                    maxHeight: "calc(100vh - 380px)",
                    display: "block",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    display: "flex",
                    gap: 6,
                    zIndex: 2,
                  }}
                >
                  <button
                    onClick={() => applyPreviewTransform("rotate")}
                    title="Rotate 90° right"
                    aria-label="Rotate 90° right"
                    style={previewIconBtnStyle}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M13.5 8a5.5 5.5 0 1 1-5.5-5.5c1.54 0 3.02.62 4.12 1.68L13.5 5.5" />
                      <path d="M13.5 2.5V5.5h-3" />
                    </svg>
                  </button>
                  <button
                    onClick={() => applyPreviewTransform("flipV")}
                    title="Flip vertically"
                    aria-label="Flip vertically"
                    style={previewIconBtnStyle}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path
                        d="M3 6L13 6L8 1Z"
                        fill="currentColor"
                        fillOpacity="0.3"
                      />
                      <path d="M3 10L13 10L8 15Z" />
                      <line
                        x1="1.5"
                        y1="8"
                        x2="14.5"
                        y2="8"
                        strokeDasharray="1.5 1.5"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() => applyPreviewTransform("flipH")}
                    title="Flip horizontally"
                    aria-label="Flip horizontally"
                    style={previewIconBtnStyle}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path
                        d="M6 3L6 13L1 8Z"
                        fill="currentColor"
                        fillOpacity="0.3"
                      />
                      <path d="M10 3L10 13L15 8Z" />
                      <line
                        x1="8"
                        y1="1.5"
                        x2="8"
                        y2="14.5"
                        strokeDasharray="1.5 1.5"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Low-quality advisory banner */}
              {lowQualityWarning && (
                <div
                  style={{
                    padding: "10px 14px",
                    borderRadius: 8,
                    background: "#fff8e1",
                    border: "1px solid #f0c040",
                    fontSize: 13,
                    color: "#7a5c00",
                  }}
                >
                  Your image may be too dark or blurry to read.
                </div>
              )}

              {/* Confirm */}
              <button
                onClick={handleConfirm}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  padding: "14px 16px",
                  borderRadius: 12,
                  background: "#2D6A4F",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 15,
                  fontWeight: 600,
                  color: "white",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#245c43";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#2D6A4F";
                }}
              >
                <svg width="16" height="13" viewBox="0 0 16 13" fill="none">
                  <path
                    d="M1.5 6.5L6 11L14.5 1.5"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Add
              </button>
            </div>
          )}
          {/* ── Gallery ── */}
          {view === "uploaded_images" && (
            <div style={{ padding: "16px 20px 20px", overflowY: "auto" }}>
              {attachments && attachments.length > 0 ? (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 8,
                  }}
                >
                  {attachments.map((a) => (
                    <div
                      key={a.id}
                      style={{
                        position: "relative",
                        aspectRatio: "1",
                        borderRadius: 8,
                        overflow: "visible",
                      }}
                    >
                      <img
                        src={a.dataUrl}
                        alt="Uploaded"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: 8,
                          border: "1.5px solid #1C6E4A",
                          display: "block",
                        }}
                      />
                      <button
                        onClick={() => onRemoveAttachment?.(a.id, a.dataUrl)}
                        style={{
                          position: "absolute",
                          top: -6,
                          right: -6,
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          background: "#475569",
                          border: "2px solid white",
                          color: "white",
                          fontSize: 12,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: 0,
                          lineHeight: 1,
                        }}
                        aria-label="Remove"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p
                  style={{
                    textAlign: "center",
                    color: "#aaa",
                    fontSize: 14,
                    margin: "24px 0",
                  }}
                >
                  No uploaded images
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
